import { CheckListApiService } from '@/codebook/check-list/check-list.api.service';
import { CheckListItem, CheckListGroup } from '@/codebook/check-list/check-list.model';
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	effect,
	inject,
	input,
	OnInit,
	output,
	signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { finalize, forkJoin, map, skip, take } from 'rxjs';
import { ProductService } from '../product.service';
import { InfiniteScrollDirective } from '@/common/infinite-scroll.directive';
import { MatCardModule } from '@angular/material/card';
import {
	CheckListItemsWithSelection,
	CheckListGroupsWithSelection,
	CheckListSelectionPayload,
	CheckListGroupsSelectionWithApi,
	CheckListItemsSelectionWithApi,
} from '../product.model';
import { MatDividerModule } from '@angular/material/divider';
import { NgClass } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';

const DEFAULT_LIMIT = 20;
const DEFAULT_OFFSET = 0;
const UNASSIGNED_GROUP_FILTER = 'is_in_any_group=false';
const ASSIGNED_GROUP_FILTER = 'is_in_any_group=true';

interface CheckListChangeEvent {
	checkpointGroups: number[];
	checkpoints: number[];
}

@Component({
	selector: 'app-product-check-list',
	standalone: true,
	imports: [
		MatCheckboxModule,
		FormsModule,
		InfiniteScrollDirective,
		MatCardModule,
		MatDividerModule,
		NgClass,
		MatButtonModule,
	],
	templateUrl: './product-check-list.component.html',
	styleUrl: './product-check-list.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductCheckListComponent implements OnInit {
	readonly editable = input.required<boolean>();

	readonly unassignedChecklists = signal<CheckListItemsWithSelection[]>([]);

	readonly assignedChecklists = signal<CheckListItemsSelectionWithApi>({
		limit: DEFAULT_LIMIT,
		offset: DEFAULT_OFFSET,
		allLoaded: false,
		isLoading: false,
		items: [],
	});

	readonly checklistGroups = signal<CheckListGroupsSelectionWithApi>({
		limit: DEFAULT_LIMIT,
		offset: DEFAULT_OFFSET,
		allLoaded: false,
		isLoading: false,
		groups: [],
	});

	readonly checkListChange = output<CheckListChangeEvent>();

	private readonly checkListApiService = inject(CheckListApiService);
	private readonly productService = inject(ProductService);

	private readonly selectedPayload = computed<CheckListSelectionPayload>(() => {
		const checkpointGroups = this.collectSelected(this.checklistGroups().groups);
		const assigned = this.collectSelected(this.assignedChecklists().items);
		const unassigned = this.collectSelected(this.unassignedChecklists());

		return {
			checkpointGroups,
			checkpoints: Array.from(new Set([...assigned, ...unassigned])),
		};
	});

	constructor() {
		effect(() => {
			this.checkListChange.emit(this.selectedPayload());
		});
	}

	ngOnInit(): void {
		this.loadInitialData();
	}

	updateGroupsSelection(selected: boolean, groupId: number): void {
		this.checklistGroups.update((state) => ({
			...state,
			groups: state.groups.map((group) => (group.id === groupId ? { ...group, selected } : group)),
		}));

		const groups = this.checklistGroups().groups;
		const selectedGroupIds = new Set(groups.filter((g) => g.selected).map((g) => g.id));

		this.assignedChecklists.update((state) => ({
			...state,
			items: state.items.map((item) => {
				const someBelongsGroupIsSelected = item.groups.some((g) => selectedGroupIds.has(g.id));

				if (!someBelongsGroupIsSelected && !item.disabled) {
					return item;
				}

				return {
					...item,
					disabled: someBelongsGroupIsSelected,
					selected: someBelongsGroupIsSelected ? false : item.selected,
				};
			}),
		}));
	}

	updateAssignedItemsSelection(selected: boolean, itemId: number): void {
		this.assignedChecklists.update((state) => ({
			...state,
			items: state.items.map((item) =>
				item.id === itemId && item.selected !== selected ? { ...item, selected } : item,
			),
		}));
	}

	updateUnassignedItemsSelection(selected: boolean, itemId: number): void {
		this.unassignedChecklists.update((items) =>
			items.map((item) => (item.id === itemId && item.selected !== selected ? { ...item, selected } : item)),
		);
	}

	loadMoreGroups(): void {
		const state = this.checklistGroups();
		if (state.isLoading || state.allLoaded) {
			return;
		}

		const nextOffset = state.offset + state.limit;

		this.checklistGroups.update((s) => ({
			...s,
			isLoading: true,
			offset: nextOffset,
		}));

		this.checkListApiService
			.getCheckListGroups(state.limit, nextOffset)
			.pipe(
				map((res) => res.results),
				map((groups) => this.mapCheckListGroupsWithSelection(groups)),
				finalize(() => {
					this.checklistGroups.update((s) => ({ ...s, isLoading: false }));
				}),
			)
			.subscribe((groups) => {
				if (groups.length === 0) {
					this.checklistGroups.update((s) => ({ ...s, allLoaded: true }));
					return;
				}

				this.checklistGroups.update((s) => ({
					...s,
					groups: [...s.groups, ...groups],
				}));
			});
	}

	loadMoreAssignedItems(): void {
		const state = this.assignedChecklists();
		if (state.isLoading || state.allLoaded) {
			return;
		}

		const nextOffset = state.offset + state.limit;

		this.assignedChecklists.update((s) => ({
			...s,
			isLoading: true,
			offset: nextOffset,
		}));

		this.checkListApiService
			.getCheckListItems(state.limit, nextOffset, ASSIGNED_GROUP_FILTER)
			.pipe(
				map((res) => this.mapCheckListItemsWithSelection(res.results)),

				finalize(() => {
					this.assignedChecklists.update((s) => ({ ...s, isLoading: false }));
				}),
			)
			.subscribe((items) => {
				if (items.length === 0) {
					this.assignedChecklists.update((s) => ({ ...s, allLoaded: true }));
					return;
				}

				this.assignedChecklists.update((s) => ({
					...s,
					items: [...s.items, ...items],
				}));
			});
	}

	changeShowState(groupId: number): void {
		this.checklistGroups.update((state) => {
			return {
				...state,
				groups: state.groups.map((group) => {
					return group.id === groupId ? { ...group, showActions: !group.showActions } : group;
				}),
			};
		});
	}

	private loadInitialData(): void {
		forkJoin({
			unassignedChecklists: this.checkListApiService
				.getCheckListItems(999, 0, UNASSIGNED_GROUP_FILTER)
				.pipe(map((res) => res.results)),
			assignedChecklists: this.checkListApiService
				.getCheckListItems(DEFAULT_LIMIT, DEFAULT_OFFSET, ASSIGNED_GROUP_FILTER)
				.pipe(map((res) => res.results)),
			checklistGroups: this.checkListApiService
				.getCheckListGroups(DEFAULT_LIMIT, DEFAULT_OFFSET)
				.pipe(map((res) => res.results)),
			dataLoaded: this.productService.dataLoaded$.pipe(skip(1), take(1)),
		}).subscribe(({ unassignedChecklists, assignedChecklists, checklistGroups }) => {
			this.unassignedChecklists.set(this.mapCheckListItemsWithSelection(unassignedChecklists));

			this.assignedChecklists.update((state) => ({
				...state,
				items: this.mapCheckListItemsWithSelection(assignedChecklists),
			}));

			this.checklistGroups.update((state) => ({
				...state,
				groups: this.mapCheckListGroupsWithSelection(checklistGroups),
			}));
		});
	}

	private mapCheckListGroupsWithSelection(checklistGroups: CheckListGroup[]): CheckListGroupsWithSelection[] {
		const { checkpointGroups = [] } = this.productService.data ?? {};

		return checklistGroups.map((group) => ({
			...group,
			selected: checkpointGroups.includes(group.id),
			showActions: false,
		}));
	}

	private mapCheckListItemsWithSelection(checklistItems: CheckListItem[]): CheckListItemsWithSelection[] {
		const { checkpoints = [], checkpointGroups = [] } = this.productService.data ?? {};

		return checklistItems.map((item) => ({
			...item,
			selected: checkpoints.includes(item.id),
			disabled: item.groups.some((g) => checkpointGroups.includes(g.id)),
		}));
	}

	private collectSelected<T extends { id: number; selected: boolean }>(items: T[]): number[] {
		return items.filter((i) => i.selected).map((i) => i.id);
	}
}
